import { create } from "zustand";
import { getDataSource } from "../data";
import { endSession, sessionGuard } from "./session";
import type { MultiQueryOutcome, QueryOutcome } from "../types/domain";

// Form state + results for the two query screens. Kept separate from
// consoleStore because it is scratch state, not a cached collection.

interface QueryState {
  // single-namespace playground
  namespace: string | null;
  text: string;
  topK: number;
  hybrid: boolean;
  running: boolean;
  result: QueryOutcome | null;
  error: string | null;

  setNamespace: (id: string) => void;
  setText: (v: string) => void;
  setTopK: (v: number) => void;
  toggleHybrid: () => void;
  run: () => Promise<void>;

  // multi-namespace fan-out
  selected: string[];
  multiText: string;
  multiTopK: number;
  multiRunning: boolean;
  multiResult: MultiQueryOutcome | null;
  multiError: string | null;

  toggleSelected: (id: string) => void;
  setSelected: (ids: string[]) => void;
  setMultiText: (v: string) => void;
  runMulti: () => Promise<void>;

  reset: () => void;
}

// Monotonic request ids: only the newest run may write results. Checking the
// namespace alone is not enough — two runs against the SAME namespace can land
// out of order, and an abandoned run would leave `running` stuck on.
let runId = 0;
let multiRunId = 0;

export const useQueryStore = create<QueryState>((set, get) => ({
  namespace: null,
  text: "how do I reset my password",
  topK: 5,
  hybrid: true,
  running: false,
  result: null,
  error: null,

  setNamespace: (namespace) => set({ namespace, result: null, error: null }),
  setText: (text) => set({ text }),
  setTopK: (topK) => set({ topK }),
  toggleHybrid: () => set((s) => ({ hybrid: !s.hybrid })),

  run: async () => {
    const { namespace, text, topK, hybrid } = get();
    if (!namespace) return;
    const alive = sessionGuard();
    const id = ++runId;
    set({ running: true, result: null, error: null });
    // A stale session drops the hits — they were matched in another tenant's
    // index — but still releases the button. Safe only because id === runId.
    const settle = () => set({ running: false });
    try {
      const result = await getDataSource().query({ namespace, text, topK, hybrid });
      if (id !== runId) return;
      if (!alive()) return settle();
      set({ result, running: false });
    } catch (e) {
      if (id !== runId) return;
      if (!alive()) return settle();
      set({ error: e instanceof Error ? e.message : String(e), running: false });
    }
  },

  selected: [],
  multiText: "how does authentication work",
  multiTopK: 8,
  multiRunning: false,
  multiResult: null,
  multiError: null,

  toggleSelected: (id) =>
    set((s) => ({
      selected: s.selected.includes(id) ? s.selected.filter((x) => x !== id) : [...s.selected, id],
      multiResult: null,
    })),
  setSelected: (selected) => set({ selected, multiResult: null }),
  setMultiText: (multiText) => set({ multiText }),

  runMulti: async () => {
    const { selected, multiText, multiTopK } = get();
    if (selected.length === 0) return;
    const alive = sessionGuard();
    const id = ++multiRunId;
    set({ multiRunning: true, multiResult: null, multiError: null });
    const settle = () => set({ multiRunning: false });
    try {
      const multiResult = await getDataSource().multiQuery({
        namespaces: selected,
        text: multiText,
        topK: multiTopK,
      });
      if (id !== multiRunId) return;
      if (!alive()) return settle();
      set({ multiResult, multiRunning: false });
    } catch (e) {
      if (id !== multiRunId) return;
      if (!alive()) return settle();
      set({ multiError: e instanceof Error ? e.message : String(e), multiRunning: false });
    }
  },

  reset: () => {
    // Invalidate anything in flight so a late response can't repopulate.
    endSession();
    runId += 1;
    multiRunId += 1;
    set({
      namespace: null, result: null, error: null, running: false,
      selected: [], multiResult: null, multiError: null, multiRunning: false,
    });
  },
}));
